import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Printer, MapPin, Phone, Instagram, Check } from 'lucide-react';
type PrintMode = 'front' | 'back' | 'both' | 'single' | null;
export function InfluencerInvitePage() {
  const [printMode, setPrintMode] = useState<PrintMode>(null);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode) {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode(null);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  const printClass = printMode ? `print-mode-${printMode}` : '';
  return <div className={`min-h-screen bg-gray-100 p-4 sm:p-8 print:p-0 print:bg-white flex flex-col items-center ${printClass}`} data-id="element-1981">
      {/* Controls - Hidden when printing */}
      <div className="max-w-[210mm] w-full mb-8 no-print" data-id="element-1982">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6" data-id="element-1983">
          <div data-id="element-1984">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1985">
              Influencer Gift Certificate
            </h1>
            <p className="text-gray-600 text-sm" data-id="element-1986">
              Print each side separately as A5, or both together on A4
              Landscape.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center" data-id="element-1987">
          <Button onClick={() => handlePrint('front')} className="gap-2 shadow-lg" data-id="element-1988">
            <Printer className="w-4 h-4" data-id="element-1989" />
            Print Front Side
          </Button>
          <Button onClick={() => handlePrint('back')} className="gap-2 shadow-lg" data-id="element-1990">
            <Printer className="w-4 h-4" data-id="element-1991" />
            Print Back Side
          </Button>
          <Button onClick={() => handlePrint('both')} variant="outline" className="gap-2" data-id="element-1992">
            <Printer className="w-4 h-4" data-id="element-1993" />
            Print Both (A4 Landscape)
          </Button>
          <Button onClick={() => handlePrint('single')} variant="outline" className="gap-2" data-id="element-1994">
            <Printer className="w-4 h-4" data-id="element-1995" />
            Print All on One Page (A4)
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-3" data-id="element-1996">
          <strong data-id="element-1997">Front / Back:</strong> Set paper to{' '}
          <strong data-id="element-1998">A5 Portrait</strong>, margins <strong data-id="element-1999">None</strong>, enable{' '}
          <strong data-id="element-2000">Background graphics</strong>.<br data-id="element-2001" />
          <strong data-id="element-2002">Both:</strong> Set paper to <strong data-id="element-2003">A4 Landscape</strong>,
          margins <strong data-id="element-2004">None</strong>, enable{' '}
          <strong data-id="element-2005">Background graphics</strong>.
        </p>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* FRONT SIDE CARD (Screen Preview + Print)   */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2006">
        Front Side
      </div>
      <div className="front-panel bg-white shadow-2xl overflow-hidden relative mb-10 print-panel" style={{
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact'
    } as React.CSSProperties} data-id="element-2007">
        {/* Background Image */}
        <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-2008" />

        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1]" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2009"></div>

        {/* Elegant Double Border Inset */}
        <div className="absolute inset-5 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.25)'
      }} data-id="element-2010"></div>
        <div className="absolute inset-7 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.10)'
      }} data-id="element-2011"></div>

        {/* Content */}
        <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-10 px-10 text-center" data-id="element-2012">
          {/* Top Branding */}
          <div className="flex flex-col items-center" data-id="element-2013">
            <div className="rounded-xl p-3 mb-4" style={{
            backgroundColor: 'rgba(255,255,255,0.95)'
          }} data-id="element-2014">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-12 w-auto object-contain" data-id="element-2015" />
            </div>
            <span className="text-2xl font-bold tracking-tight leading-none mb-2" style={{
            color: '#ffffff',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }} data-id="element-2016">
              DenticoClinic
            </span>
            <p className="text-sm uppercase leading-tight" style={{
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.3em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2017">
              Private Dentistry
            </p>
          </div>

          {/* Center Content */}
          <div className="flex flex-col items-center w-full" data-id="element-2018">
            <div className="w-12 h-px mb-7" style={{
            backgroundColor: 'rgba(255,255,255,0.4)'
          }} data-id="element-2019"></div>

            <p className="text-xs uppercase font-medium mb-5" style={{
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: '0.4em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2020">
              Gift Certificate
            </p>

            <h2 className="text-4xl font-serif mb-8 leading-tight" style={{
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
          }} data-id="element-2021">
              Professional
              <br data-id="element-2022" />
              Teeth Whitening
            </h2>

            <div className="inline-block py-4 px-12" style={{
            borderTop: '1px solid rgba(255,255,255,0.35)',
            borderBottom: '1px solid rgba(255,255,255,0.35)'
          }} data-id="element-2023">
              <p className="text-[10px] uppercase mb-2" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.3em'
            }} data-id="element-2024">
                Value
              </p>
              <p className="text-3xl font-serif font-bold tracking-wide" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-2025">
                £280
              </p>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-auto" data-id="element-2026">
            <p className="text-xs" style={{
            color: 'rgba(255,255,255,0.65)',
            letterSpacing: '0.2em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2027">
              denticoclinic.co.uk
            </p>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BACK SIDE CARD (Screen Preview + Print)    */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2028">
        Back Side
      </div>
      <div className="back-panel overflow-hidden relative print-panel" style={{
      backgroundColor: '#fafaf9',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-2029">
        {/* Elegant Border */}
        <div className="absolute inset-5 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.10)'
      }} data-id="element-2030"></div>
        <div className="absolute inset-7 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.05)'
      }} data-id="element-2031"></div>

        {/* Content wrapper */}
        <div className="relative z-10 flex flex-col py-8 px-10 items-center text-center h-full" data-id="element-2032">
          {/* Top Branding */}
          <div className="flex flex-col items-center mb-5 mt-1" data-id="element-2033">
            <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-8 w-auto object-contain mb-1.5" data-id="element-2034" />
            <span className="text-base font-bold tracking-tight leading-none mb-1" style={{
            color: '#111827'
          }} data-id="element-2035">
              Dentico
              <span style={{
              color: '#0d9488'
            }} data-id="element-2036">
                Clinic
              </span>
            </span>
            <p className="text-[9px] uppercase leading-tight" style={{
            color: '#6b7280',
            letterSpacing: '0.2em'
          }} data-id="element-2037">
              Private Dentistry
            </p>
          </div>

          {/* Invitation Text */}
          <h1 className="text-xl font-serif mb-4 tracking-wide" style={{
          color: '#134e4a'
        }} data-id="element-2038">
            Exclusive Invitation
          </h1>

          <div className="max-w-[88%] space-y-3 mb-5" data-id="element-2039">
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2040">
              We are delighted to offer you a complimentary professional teeth
              whitening experience at Dentico Dental Clinic.
            </p>
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2041">
              As part of our collaboration, we invite you to visit our clinic,
              experience our modern digital dentistry, and share your experience
              with your audience.
            </p>
          </div>

          {/* What's Included */}
          <div className="mb-5 w-full max-w-[85%]" data-id="element-2042">
            <h3 className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{
            color: '#115e59'
          }} data-id="element-2043">
              What's Included
            </h3>
            <ul className="space-y-2 text-left inline-block" data-id="element-2044">
              {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-2045">
                  <div className="mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#ccfbf1',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
              } as React.CSSProperties} data-id="element-2046">
                    <Check className="w-2 h-2" style={{
                  color: '#0f766e'
                }} data-id="element-2047" />
                  </div>
                  <span className="text-[13px]" style={{
                color: '#374151'
              }} data-id="element-2048">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Unique Code & Validity */}
          <div className="w-full max-w-[180px] mb-5" data-id="element-2049">
            <div className="mb-3" data-id="element-2050">
              <label className="block text-[9px] uppercase tracking-widest mb-1" style={{
              color: '#6b7280'
            }} data-id="element-2051">
                Unique Code
              </label>
              <div contentEditable suppressContentEditableWarning className="w-full text-sm font-mono text-center pb-1 min-h-[1.5em] focus:outline-none" style={{
              color: '#374151',
              borderBottom: '1px solid #d1d5db'
            }} data-id="element-2052">
                DC-
              </div>
            </div>
            <p className="text-[10px] text-center tracking-wider" style={{
            color: '#6b7280'
          }} data-id="element-2053">
              Valid until: _______________
            </p>
          </div>

          {/* Footer Section */}
          <div className="mt-auto w-full flex flex-col items-center" data-id="element-2054">
            <p className="text-[9px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-2055">
              By appointment only • Non-transferable
            </p>

            {/* Address + Contact Combined */}
            <div className="flex flex-col items-center text-[10px] mb-4 leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2056">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2057">
                <MapPin className="w-2.5 h-2.5" style={{
                color: '#0d9488'
              }} data-id="element-2058" />
                <span className="font-medium" style={{
                color: '#374151'
              }} data-id="element-2059">
                  Central London – Angel
                </span>
              </div>
              <span data-id="element-2060">360–364 City Road, London EC1V 2PY</span>
              <div className="flex items-center justify-center gap-4 mt-1.5" data-id="element-2061">
                <div className="flex items-center gap-1" data-id="element-2062">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2063" />
                  <span data-id="element-2064">+44 7345 197820</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-2065">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2066" />
                  <span data-id="element-2067">@dentico_clinic</span>
                </div>
              </div>
            </div>

            {/* Two QR Codes Row */}
            <div className="flex items-start justify-center gap-8 w-full" data-id="element-2068">
              {/* QR 1: Instagram */}
              <div className="flex flex-col items-center" data-id="element-2069">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-2070">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR Code" className="w-full h-full object-contain" data-id="element-2071" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-2072">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2073" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-2074">
                    Follow us
                  </p>
                </div>
              </div>

              {/* QR 2: WhatsApp */}
              <div className="flex flex-col items-center" data-id="element-2075">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-2076">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR Code" className="w-full h-full object-contain" data-id="element-2077" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-2078">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2079" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-2080">
                    via WhatsApp
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BOTH SIDES ON ONE PAGE (A4 Landscape Preview) */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 mt-6 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2081">
        Both Sides — One Page
      </div>
      <div className="single-panel overflow-hidden relative print-panel-single flex" style={{
      backgroundColor: '#ffffff',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-2082">
        {/* Fold line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed z-50" style={{
        borderColor: 'rgba(200,200,200,0.4)'
      }} data-id="element-2083"></div>

        {/* LEFT — Front Side */}
        <div className="w-1/2 h-full relative flex flex-col items-center justify-center text-center" style={{
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2084">
          <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-2085" />
          <div className="absolute inset-0 z-[1]" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact'
        } as React.CSSProperties} data-id="element-2086"></div>
          <div className="absolute inset-4 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.25)'
        }} data-id="element-2087"></div>
          <div className="absolute inset-6 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.10)'
        }} data-id="element-2088"></div>

          <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-8 px-8" data-id="element-2089">
            <div className="flex flex-col items-center" data-id="element-2090">
              <div className="rounded-xl p-2.5 mb-3" style={{
              backgroundColor: 'rgba(255,255,255,0.95)'
            }} data-id="element-2091">
                <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-10 w-auto object-contain" data-id="element-2092" />
              </div>
              <span className="text-xl font-bold tracking-tight leading-none mb-1.5" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-2093">
                DenticoClinic
              </span>
              <p className="text-xs uppercase leading-tight" style={{
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.3em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2094">
                Private Dentistry
              </p>
            </div>
            <div className="flex flex-col items-center w-full" data-id="element-2095">
              <div className="w-10 h-px mb-5" style={{
              backgroundColor: 'rgba(255,255,255,0.4)'
            }} data-id="element-2096"></div>
              <p className="text-[10px] uppercase font-medium mb-4" style={{
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: '0.4em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2097">
                Gift Certificate
              </p>
              <h2 className="text-3xl font-serif mb-6 leading-tight" style={{
              color: '#ffffff',
              textShadow: '0 1px 4px rgba(0,0,0,0.3)'
            }} data-id="element-2098">
                Professional
                <br data-id="element-2099" />
                Teeth Whitening
              </h2>
              <div className="inline-block py-3 px-10" style={{
              borderTop: '1px solid rgba(255,255,255,0.35)',
              borderBottom: '1px solid rgba(255,255,255,0.35)'
            }} data-id="element-2100">
                <p className="text-[9px] uppercase mb-1.5" style={{
                color: 'rgba(255,255,255,0.65)',
                letterSpacing: '0.3em'
              }} data-id="element-2101">
                  Value
                </p>
                <p className="text-2xl font-serif font-bold tracking-wide" style={{
                color: '#ffffff',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} data-id="element-2102">
                  £280
                </p>
              </div>
            </div>
            <div className="mt-auto" data-id="element-2103">
              <p className="text-[10px]" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.2em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2104">
                denticoclinic.co.uk
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT — Back Side */}
        <div className="w-1/2 h-full relative flex flex-col py-6 px-8 items-center text-center" style={{
        backgroundColor: '#fafaf9',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2105">
          <div className="absolute inset-4 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.10)'
        }} data-id="element-2106"></div>
          <div className="absolute inset-6 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.05)'
        }} data-id="element-2107"></div>

          <div className="relative z-10 flex flex-col items-center h-full w-full" data-id="element-2108">
            {/* Logo */}
            <div className="flex flex-col items-center mb-4 mt-1" data-id="element-2109">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-7 w-auto object-contain mb-1" data-id="element-2110" />
              <span className="text-sm font-bold tracking-tight leading-none mb-0.5" style={{
              color: '#111827'
            }} data-id="element-2111">
                Dentico
                <span style={{
                color: '#0d9488'
              }} data-id="element-2112">
                  Clinic
                </span>
              </span>
              <p className="text-[8px] uppercase leading-tight" style={{
              color: '#6b7280',
              letterSpacing: '0.2em'
            }} data-id="element-2113">
                Private Dentistry
              </p>
            </div>

            <h1 className="text-lg font-serif mb-3 tracking-wide" style={{
            color: '#134e4a'
          }} data-id="element-2114">
              Exclusive Invitation
            </h1>

            <div className="max-w-[90%] space-y-2 mb-4" data-id="element-2115">
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2116">
                We are delighted to offer you a complimentary professional teeth
                whitening experience at Dentico Dental Clinic.
              </p>
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2117">
                As part of our collaboration, we invite you to visit our clinic,
                experience our modern digital dentistry, and share your
                experience with your audience.
              </p>
            </div>

            <div className="mb-4 w-full max-w-[85%]" data-id="element-2118">
              <h3 className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{
              color: '#115e59'
            }} data-id="element-2119">
                What's Included
              </h3>
              <ul className="space-y-1.5 text-left inline-block" data-id="element-2120">
                {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-2121">
                    <div className="mt-0.5 w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: '#ccfbf1',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                } as React.CSSProperties} data-id="element-2122">
                      <Check className="w-1.5 h-1.5" style={{
                    color: '#0f766e'
                  }} data-id="element-2123" />
                    </div>
                    <span className="text-[11px]" style={{
                  color: '#374151'
                }} data-id="element-2124">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex items-end justify-center gap-6 mb-3" data-id="element-2125">
              <div data-id="element-2126">
                <label className="block text-[8px] uppercase tracking-widest mb-0.5" style={{
                color: '#6b7280'
              }} data-id="element-2127">
                  Unique Code
                </label>
                <div contentEditable suppressContentEditableWarning className="text-xs font-mono text-center pb-0.5 min-h-[1.3em] focus:outline-none" style={{
                color: '#374151',
                borderBottom: '1px solid #d1d5db',
                minWidth: '70px'
              }} data-id="element-2128">
                  DC-
                </div>
              </div>
              <p className="text-[9px] tracking-wider pb-0.5" style={{
              color: '#6b7280'
            }} data-id="element-2129">
                Valid until: _______________
              </p>
            </div>

            <p className="text-[8px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-2130">
              By appointment only • Non-transferable
            </p>

            {/* Footer */}
            <div className="mt-auto w-full flex flex-col items-center" data-id="element-2131">
              <div className="flex flex-col items-center text-[9px] mb-3 leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2132">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-2133">
                  <MapPin className="w-2 h-2" style={{
                  color: '#0d9488'
                }} data-id="element-2134" />
                  <span className="font-medium" style={{
                  color: '#374151'
                }} data-id="element-2135">
                    Central London – Angel
                  </span>
                </div>
                <span data-id="element-2136">360–364 City Road, London EC1V 2PY</span>
                <div className="flex items-center justify-center gap-3 mt-1" data-id="element-2137">
                  <div className="flex items-center gap-1" data-id="element-2138">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2139" />
                    <span data-id="element-2140">+44 7345 197820</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2141">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2142" />
                    <span data-id="element-2143">@dentico_clinic</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-center gap-6" data-id="element-2144">
                <div className="flex flex-col items-center" data-id="element-2145">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-2146">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR" className="w-full h-full object-contain" data-id="element-2147" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-2148">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2149" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-2150">
                      Follow us
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center" data-id="element-2151">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-2152">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR" className="w-full h-full object-contain" data-id="element-2153" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-2154">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2155" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-2156">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* PRINT STYLES                               */}
      {/* ═══════════════════════════════════════════ */}
      <style data-id="element-2157">{`
        /* ── Screen preview ── */
        @media screen {
          .print-panel {
            width: 100%;
            max-width: 148.5mm;
            aspect-ratio: 148.5 / 210;
          }
          .print-panel-single {
            width: 100%;
            max-width: 297mm;
            aspect-ratio: 297 / 210;
          }
        }

        /* ── Print: shared rules ── */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          svg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-panel {
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
        }

        /* ── Print FRONT only ── */
        @media print {
          .print-mode-front .back-panel {
            display: none !important;
          }
          .print-mode-front .front-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-front {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BACK only ── */
        @media print {
          .print-mode-back .front-panel {
            display: none !important;
          }
          .print-mode-back .back-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-back {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BOTH (A4 Landscape, side by side) ── */
        @media print {
          .print-mode-both {
            padding: 0 !important;
            margin: 0 !important;
          }
          .print-mode-both .front-panel,
          .print-mode-both .back-panel {
            width: 148.5mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            display: inline-block !important;
            vertical-align: top !important;
            float: left !important;
          }
          .print-mode-both .single-panel {
            display: none !important;
          }
        }

        /* ── Print SINGLE (A4 Landscape, both sides on one page) ── */
        @media print {
          .print-mode-single .front-panel,
          .print-mode-single .back-panel {
            display: none !important;
          }
          .print-mode-single .single-panel {
            width: 297mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            display: flex !important;
          }
          .print-mode-single {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Hide single panel in front/back modes ── */
        @media print {
          .print-mode-front .single-panel,
          .print-mode-back .single-panel {
            display: none !important;
          }
        }

        /* ── @page rules per mode ── */
        .print-mode-front ~ style,
        .print-mode-back ~ style {
          /* Handled by dynamic @page below */
        }
      `}</style>

      {/* Dynamic @page size based on print mode */}
      {printMode === 'front' || printMode === 'back' ? <style data-id="element-2158">{`
          @media print {
            @page {
              size: 148mm 210mm;
              margin: 0;
            }
          }
        `}</style> : printMode === 'both' ? <style data-id="element-2159">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : printMode === 'single' ? <style data-id="element-2160">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : null}
    </div>;
}